// Per-user video library store.
// Two implementations: localStorage (mock auth / offline) and API-backed (Cognito + API Gateway).

import {
  bookmarkVideo,
  deleteVideo,
  getVideoDetail,
  listVideos,
} from "./api-client";

export type VideoStatus =
  | "bookmarked"
  | "transcribing"
  | "quota-exceeded"
  | "ready"
  | "in-progress"
  | "complete";

export interface UserVideo {
  youtubeVideoId: string;
  title: string;
  channel: string;
  thumbnailUrl: string;
  status: VideoStatus;
  savedAt: number; // unix ms
  // M2.4: skill tags projected from video_content. Optional because local
  // (mock-auth) stores don't carry them; the API store always populates them
  // (possibly with null/[] for not-yet-transcribed videos).
  skillCategory?: string | null;
  difficulty?: string | null;
  techniques?: string[];
}

export interface LibraryStore {
  list(): Promise<UserVideo[]>;
  get(youtubeVideoId: string): Promise<UserVideo | null>;
  add(video: UserVideo): Promise<void>;
  remove(youtubeVideoId: string): Promise<void>;
  update(youtubeVideoId: string, patch: Partial<UserVideo>): Promise<UserVideo | null>;
}

export type TokenProvider = () => Promise<string | null>;

async function requireToken(getToken: TokenProvider): Promise<string> {
  const t = await getToken();
  if (!t) throw new Error("Sign in again to continue.");
  return t;
}

function storageKey(userSub: string): string {
  return `fretboard.library.${userSub}`;
}

export function createLocalLibraryStore(userSub: string): LibraryStore {
  const key = storageKey(userSub);

  function readAll(): UserVideo[] {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as UserVideo[]) : [];
    } catch {
      return [];
    }
  }

  function writeAll(videos: UserVideo[]): void {
    localStorage.setItem(key, JSON.stringify(videos));
  }

  return {
    async list() {
      return readAll().sort((a, b) => b.savedAt - a.savedAt);
    },
    async get(id) {
      return readAll().find((v) => v.youtubeVideoId === id) ?? null;
    },
    async add(video) {
      const all = readAll();
      const existing = all.findIndex((v) => v.youtubeVideoId === video.youtubeVideoId);
      if (existing >= 0) {
        all[existing] = { ...all[existing], ...video };
      } else {
        all.push(video);
      }
      writeAll(all);
    },
    async remove(id) {
      writeAll(readAll().filter((v) => v.youtubeVideoId !== id));
    },
    async update(id, patch) {
      const all = readAll();
      const idx = all.findIndex((v) => v.youtubeVideoId === id);
      if (idx < 0) return null;
      const updated: UserVideo = { ...all[idx], ...patch };
      all[idx] = updated;
      writeAll(all);
      return updated;
    },
  };
}

// API-backed library store. Status is derived server-side, so `update` is a
// pure refresh — the patch is ignored (intentional; kept for interface parity).
export function createApiLibraryStore(getToken: TokenProvider): LibraryStore {
  return {
    async list() {
      return listVideos(await requireToken(getToken));
    },
    async get(id) {
      const detail = await getVideoDetail(id, await requireToken(getToken));
      return detail?.video ?? null;
    },
    async add(video) {
      await bookmarkVideo(video.youtubeVideoId, await requireToken(getToken));
    },
    async remove(id) {
      await deleteVideo(id, await requireToken(getToken));
    },
    async update(id, _patch) {
      const detail = await getVideoDetail(id, await requireToken(getToken));
      return detail?.video ?? null;
    },
  };
}
